<template>
  <SelectionTable
    :columns="tableColumns"
    :items="volumeItems"
    :scroll-to-item="preselectedVersions?.[0] ?? preselectedReadNumbers?.[0]"
    @touch-swipe="handleTouchSwipe"
  >
    <template #cell-0="{ item }">
      <div
        :class="[
          'rounded-l-[calc(var(--border-radius)*1px)] rounded-r-[3px] text-text bg-main-background flex h-7.5 justify-center items-center relative w-full',
          $typo.openSans12,
        ]"
      >
        {{
          versionHasSuffix(item)
            ? t('bulkModalVolumeLabelWithSuffix', {
                number: formatVolumeNumberFromVersion(item),
                volumeSuffix: getVolumeSuffixFromId(getSuffixFromVolumeVersion(item)).abbreviation,
              })
            : t('bulkModalVolumeLabel', { number: formatVolumeNumberFromVersion(item) })
        }}
      </div>
    </template>
    <template #cell-1="{ item, setRef }">
      <div
        :class="[
          'rounded-[3px] text-text flex h-7.5 justify-center items-center relative w-full gap-1',
          $typo.openSans12,
          ownedMap[item] ? 'bg-(--atsume-main)' : 'bg-button',
          {
            'opacity-50': notReleasedVersionMap[item],
          },
        ]"
        :data-test-id="'volume-selection-table-cell'"
        :data-active="!!ownedMap[item]"
        :ref="(el: any) => setRef(el)"
        :data-item="item"
      >
        <IconCustom
          v-if="preselectedIcon && preselectedVersionSet.has(item)"
          :ligature="preselectedIcon"
          size="16"
        />
        {{ t('releaseSelectionTableOwnedCell') }}
      </div>
    </template>
    <template #cell-2="{ item, setRef, rowSpan }">
      <div
        :class="[
          'rounded-r-[calc(var(--border-radius)*1px)] rounded-l-[3px] text-text flex justify-center items-center relative w-full gap-1',
          $typo.openSans12,
          readMap[getVolumeNumberFromVersion(item)] ? 'bg-(--atsume-main)' : 'bg-button',
          {
            'opacity-50': notReleasedNumberMap[getVolumeNumberFromVersion(item)],
          },
          rowSpan > 1 ? 'h-full' : 'h-7.5',
        ]"
        :ref="(el: any) => setRef(el)"
        :data-active="readMap[getVolumeNumberFromVersion(item)]"
        :data-item="getVolumeNumberFromVersion(item)"
      >
        <IconCustom
          v-if="preselectedIcon && preselectedReadNumberSet.has(getVolumeNumberFromVersion(item))"
          :ligature="preselectedIcon"
          size="16"
        />
        {{ t('releaseSelectionTableReadCell') }}
      </div>
    </template>
  </SelectionTable>
</template>

<script lang="ts" setup>
import { computed, onMounted, ref, toRefs, watch } from 'vue'
import SelectionTable, { type ColumnConfig } from './SelectionTable.vue'
import { useLocalizationStore } from '@/stores/localization.ts'
import $typo from '@/styles/typografy.module.css'
import {
  formatVolumeNumberFromVersion,
  getSuffixFromVolumeVersion,
  getVolumeNumberFromVersion,
  OwnedSelectionMap,
  ReadSelectionMap,
  versionHasSuffix,
} from '@/utils'
import type { IconName } from '@/icons'
import IconCustom from './IconCustom.vue'

interface Props {
  sortedVolumeNumbers: string[]
  notReleasedVolumeNumbers: string[]
  sortedVolumeNumbersWithoutSuffix: string[]
  notReleasedVolumeNumbersWithoutSuffix: string[]
  ownedMap: OwnedSelectionMap
  readMap: ReadSelectionMap
  preselectedVersions?: string[]
  preselectedReadNumbers?: string[]
  preselectedIcon?: IconName
}

const { getVolumeSuffixFromId } = useLocalizationStore()
const props = defineProps<Props>()

const {
  sortedVolumeNumbers,
  sortedVolumeNumbersWithoutSuffix,
  notReleasedVolumeNumbersWithoutSuffix,
  notReleasedVolumeNumbers,
  ownedMap,
  readMap,
  preselectedVersions,
  preselectedReadNumbers,
  preselectedIcon,
} = toRefs(props)

const emit = defineEmits<{
  (e: 'update:ownedMap', value: OwnedSelectionMap): void
  (e: 'update:readMap', value: ReadSelectionMap): void
  (e: 'editOwnedVolumeEntry', value: string): void
}>()

const { t } = useLocalizationStore()

const preselectedVersionSet = computed(() => new Set(preselectedVersions.value))
const preselectedReadNumberSet = computed(() => new Set(preselectedReadNumbers.value))

const reverseVolumeOrder = ref(false)
const allOwnedSelection = ref(false)
const allReadSelection = ref(false)

const volumeItems = computed(() => {
  const sortedVolumeNumbersReversed = reverseVolumeOrder.value
    ? [...sortedVolumeNumbers.value].reverse()
    : sortedVolumeNumbers.value
  const sortedVolumeNumbersAsLabels = sortedVolumeNumbersReversed.map((version) => ({
    version,
    number: Number(getVolumeNumberFromVersion(version)),
    suffixAbbreviation: versionHasSuffix(version)
      ? getVolumeSuffixFromId(getSuffixFromVolumeVersion(version)).abbreviation
      : undefined,
  }))
  sortedVolumeNumbersAsLabels.sort((a, b) => {
    if (a.number === b.number) {
      if (a.suffixAbbreviation && b.suffixAbbreviation) {
        return a.suffixAbbreviation.localeCompare(b.suffixAbbreviation)
      } else if (a.suffixAbbreviation) {
        return 1
      } else if (b.suffixAbbreviation) {
        return -1
      } else {
        return 0
      }
    }
    return 0
  })
  return sortedVolumeNumbersAsLabels.map(({ version }) => version)
})

const releasedVolumeNumbersWithoutSuffix = computed(() =>
  sortedVolumeNumbersWithoutSuffix.value.filter(
    (number) => !notReleasedVolumeNumbersWithoutSuffix.value.includes(number),
  ),
)

const notReleasedNumberMap = computed(() =>
  Object.fromEntries(notReleasedVolumeNumbersWithoutSuffix.value.map((n) => [n, true])),
)

const notReleasedVersionMap = computed(() =>
  Object.fromEntries(notReleasedVolumeNumbers.value.map((v) => [v, true])),
)

const tableColumns = computed<ColumnConfig<string>[]>(() => [
  {
    headerButton: {
      ligature: reverseVolumeOrder.value ? 'arrow_upward' : 'arrow_downward',
      onClick: () => (reverseVolumeOrder.value = !reverseVolumeOrder.value),
    },
    onCellLongPress: () => {},
  },
  {
    headerButton: {
      ligature: allOwnedSelection.value ? 'deselect' : 'select_all',
      onClick: () => setAllOwned(allOwnedSelection.value ? 0 : 1),
    },
    onCellClick: toggleOwned,
    onCellLongPress: () => {},
  },
  {
    headerButton: {
      ligature: allReadSelection.value ? 'deselect' : 'select_all',
      onClick: () => setAllRead(!allReadSelection.value),
    },
    onCellClick: (item: string) => toggleRead(getVolumeNumberFromVersion(item)),
    groupBy: getVolumeNumberFromVersion,
  },
])

watch(
  () => ownedMap.value,
  () => {
    const allOwned = releasedVolumeNumbersWithoutSuffix.value.every((n) => ownedMap.value[n] > 0)
    const noneOwned = releasedVolumeNumbersWithoutSuffix.value.every((n) => ownedMap.value[n] === 0)
    allOwnedSelection.value = allOwned || (!noneOwned && allOwnedSelection.value)
  },
  { deep: true },
)

watch(
  () => readMap.value,
  () => {
    const allRead = releasedVolumeNumbersWithoutSuffix.value.every((n) => readMap.value[n])
    const noneRead = releasedVolumeNumbersWithoutSuffix.value.every((n) => !readMap.value[n])
    allReadSelection.value = allRead || (!noneRead && allReadSelection.value)
  },
  { deep: true },
)

onMounted(() => {
  if (preselectedVersions.value) {
    const newOwnedMap = { ...ownedMap.value }
    for (const version of preselectedVersions.value) {
      if (!newOwnedMap[version]) {
        newOwnedMap[version] = 1
      }
    }
    emit('update:ownedMap', newOwnedMap)
  }
  if (preselectedReadNumbers.value) {
    const newReadMap = { ...readMap.value }
    for (const number of preselectedReadNumbers.value) {
      if (!newReadMap[number]) {
        newReadMap[number] = true
      }
    }
    emit('update:readMap', newReadMap)
  }
})

function setAllOwned(n: number) {
  allOwnedSelection.value = n === 1
  const newOwnedMap = { ...ownedMap.value }
  const volumesToSelect = allOwnedSelection.value
    ? releasedVolumeNumbersWithoutSuffix.value
    : sortedVolumeNumbersWithoutSuffix.value
  volumesToSelect.forEach((number) => {
    newOwnedMap[number] = n
  })
  emit('update:ownedMap', newOwnedMap)
}

function setAllRead(n: boolean) {
  allReadSelection.value = n
  const volumesToSelect = allReadSelection.value
    ? releasedVolumeNumbersWithoutSuffix.value
    : sortedVolumeNumbersWithoutSuffix.value
  const newReadMap = { ...readMap.value }
  volumesToSelect.forEach((number) => {
    newReadMap[number] = n
  })
  emit('update:readMap', newReadMap)
}

function toggleOwned(number: string) {
  const newOwnedMap = { ...ownedMap.value }
  newOwnedMap[number] = newOwnedMap[number] ? 0 : 1
  emit('update:ownedMap', newOwnedMap)
}

function toggleRead(number: string) {
  const newReadMap = { ...readMap.value }
  newReadMap[number] = !newReadMap[number]
  emit('update:readMap', newReadMap)
}

function handleTouchSwipe({
  columnIndex,
  item,
  swipeMode,
}: {
  columnIndex: number
  item: string
  swipeMode: boolean
}) {
  if (columnIndex === 1) {
    const newOwnedMap = { ...ownedMap.value }
    newOwnedMap[item] = swipeMode ? 1 : 0
    emit('update:ownedMap', newOwnedMap)
  } else if (columnIndex === 2) {
    const newReadMap = { ...readMap.value }
    newReadMap[getVolumeNumberFromVersion(item)] = swipeMode
    emit('update:readMap', newReadMap)
  }
}
</script>
